import React, { useState, useEffect } from 'react';
import {
    Card,
    Row,
    Col,
    Typography,
    Space,
    Statistic,
    Progress,
    Button,
    Alert,
    Spin,
    Timeline
} from 'antd';
import {
    PictureOutlined,
    TagsOutlined,
    UploadOutlined,
    DesktopOutlined,
    SortAscendingOutlined,
    CloudUploadOutlined,
    FileImageOutlined,
    RightOutlined,
    SyncOutlined,
    ClockCircleOutlined,
    TrophyOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ApiService } from '../services/api';
import { UploadRecord } from '../types';

const { Title, Text, Paragraph } = Typography;

interface DashboardStats {
    totalImages: number;
    totalTags: number;
    untaggedImages: number;
    undistributedImages: number;
    totalDeviceTypes: number;
    imagesBySize: Array<{ width: number; height: number; count: number }>;
}

interface SystemStatus {
    cpuUsage: number;
    memoryUsage: number;
    memoryTotal: number;
    memoryUsed: number;
    memoryFree: number;
}

interface QuickAction {
    title: string;
    description: string;
    icon: React.ReactNode;
    path: string;
    color: string;
    bgColor: string;
}

interface RecentActivity {
    id: string;
    type: 'upload' | 'tag' | 'device' | 'category';
    title: string;
    description: string;
    time: string;
    icon: React.ReactNode;
    color: string;
}

const Dashboard: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState<DashboardStats>({
        totalImages: 0,
        totalTags: 0,
        untaggedImages: 0,
        undistributedImages: 0,
        totalDeviceTypes: 0,
        imagesBySize: []
    });
    const [systemStatus, setSystemStatus] = useState<SystemStatus>({
        cpuUsage: 0,
        memoryUsage: 0,
        memoryTotal: 0,
        memoryUsed: 0,
        memoryFree: 0
    });
    const [recentUploads, setRecentUploads] = useState<UploadRecord[]>([]);
    const [loadError, setLoadError] = useState<string | null>(null);

    const quickActions: QuickAction[] = [
        {
            title: '图片上传',
            description: '批量上传图片文件，支持多种格式',
            icon: <CloudUploadOutlined />,
            path: '/upload',
            color: '#1890ff',
            bgColor: '#e6f7ff'
        },
        {
            title: '图片管理',
            description: '浏览和管理所有上传的图片',
            icon: <FileImageOutlined />,
            path: '/image-list',
            color: '#52c41a',
            bgColor: '#f6ffed'
        },
        {
            title: '标签管理',
            description: '创建和编辑图片标签分类',
            icon: <TagsOutlined />,
            path: '/tag-management',
            color: '#fa8c16',
            bgColor: '#fff7e6'
        },
        {
            title: '图片标记',
            description: '为无标签图片添加分类标签',
            icon: <PictureOutlined />,
            path: '/image-list',
            color: '#eb2f96',
            bgColor: '#fff0f6'
        },
        {
            title: '设备管理',
            description: '管理LED点阵屏设备类型',
            icon: <DesktopOutlined />,
            path: '/device-type-management',
            color: '#722ed1',
            bgColor: '#f9f0ff'
        },
        {
            title: '分类管理',
            description: '管理图片排序分类规则',
            icon: <SortAscendingOutlined />,
            path: '/sort-category-management',
            color: '#13c2c2',
            bgColor: '#e6fffb'
        }
    ];

    // 生成最近活动数据
    const getRecentActivities = (): RecentActivity[] => {
        return recentUploads.slice(0, 5).map((upload, index) => ({
            id: upload.id.toString(),
            type: 'upload' as const,
            title: '批量上传图片',
            description: `${upload.username} 上传了${upload.total_images}张图片，成功${upload.success_count}张`,
            time: getRelativeTime(upload.created_at),
            icon: <CloudUploadOutlined />,
            color: upload.status === 'success' ? '#52c41a' : upload.status === 'partial' ? '#fa8c16' : '#ff4d4f'
        }));
    };

    // 计算相对时间
    const getRelativeTime = (dateString: string): string => {
        const now = new Date();
        const uploadTime = new Date(dateString);
        const diffInHours = Math.floor((now.getTime() - uploadTime.getTime()) / (1000 * 60 * 60));

        if (diffInHours < 1) {
            const diffInMinutes = Math.floor((now.getTime() - uploadTime.getTime()) / (1000 * 60));
            return diffInMinutes <= 0 ? '刚刚' : `${diffInMinutes}分钟前`;
        } else if (diffInHours < 24) {
            return `${diffInHours}小时前`;
        } else {
            const diffInDays = Math.floor(diffInHours / 24);
            return `${diffInDays}天前`;
        }
    };

    useEffect(() => {
        loadDashboardStats();
    }, []);

    const loadDashboardStats = async () => {
        setLoading(true);
        setLoadError(null);

        try {
            // 并行加载所有数据
            const [statsRes, statusRes, uploadsRes] = await Promise.all([
                ApiService.getStats(),
                ApiService.getMonitorStatus(),
                ApiService.getUploadRecordsList({
                    page: 1,
                    page_size: 5
                })
            ]);

            if (statsRes.code === 200) {
                setStats({
                    totalImages: statsRes.data.total_images,
                    totalTags: statsRes.data.total_tags,
                    untaggedImages: statsRes.data.untagged_images,
                    undistributedImages: statsRes.data.undistributed_images,
                    totalDeviceTypes: statsRes.data.total_device_types,
                    imagesBySize: statsRes.data.images_by_sizes
                });
            }

            if (statusRes.code === 200) {
                setSystemStatus({
                    cpuUsage: statusRes.data.cpu_usage,
                    memoryUsage: statusRes.data.memory_usage,
                    memoryTotal: statusRes.data.memory_total,
                    memoryUsed: statusRes.data.memory_used,
                    memoryFree: statusRes.data.memory_free
                });
            }

            if (uploadsRes.code === 200) {
                if (uploadsRes.data.records && uploadsRes.data.records.length > 0) {
                    setRecentUploads(uploadsRes.data.records);
                }
            }

        } catch (error: any) {
            console.error('加载Dashboard数据失败:', error);
            setLoadError(error.response?.data?.message || error.message || '加载数据失败');
        } finally {
            setLoading(false);
        }
    };

    const handleQuickAction = (path: string) => {
        navigate(path);
    };

    const getWelcomeTime = () => {
        const hour = new Date().getHours();
        if (hour < 6) return '凌晨好';
        if (hour < 9) return '早上好';
        if (hour < 12) return '上午好';
        if (hour < 14) return '中午好';
        if (hour < 18) return '下午好';
        if (hour < 22) return '晚上好';
        return '夜深了';
    };

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '100px 0' }}>
                <Spin size="large" />
                <div style={{ marginTop: 16 }}>
                    <Text type="secondary">正在加载Dashboard数据...</Text>
                </div>
            </div>
        );
    }

    if (loadError) {
        return (
            <div style={{ padding: '50px 0' }}>
                <Alert
                    message="加载失败"
                    description={`无法加载Dashboard数据: ${loadError}`}
                    type="error"
                    showIcon
                    action={
                        <Button onClick={loadDashboardStats}>重试</Button>
                    }
                />
            </div>
        );
    }

    return (
        <div style={{ padding: 0 }}>
            {/* 欢迎横幅 */}
            <Card style={{ marginBottom: 24, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', border: 'none' }}>
                <Row align="middle">
                    <Col flex="auto">
                        <div style={{ color: 'white' }}>
                            <Title level={2} style={{ color: 'white', marginBottom: 8 }}>
                                {getWelcomeTime()}，{user?.username}！
                            </Title>
                            <Paragraph style={{ color: 'rgba(255,255,255,0.85)', marginBottom: 0, fontSize: 16 }}>
                                欢迎使用 Pixel Creator 管理系统，开始管理您的LED点阵屏图片资源
                            </Paragraph>
                        </div>
                    </Col>
                    <Col>
                        <Space>
                            <Button
                                type="primary"
                                ghost
                                icon={<SyncOutlined />}
                                onClick={loadDashboardStats}
                                loading={loading}
                                style={{ borderColor: 'white', color: 'white' }}
                            >
                                刷新数据
                            </Button>
                            <TrophyOutlined style={{ fontSize: 48, color: 'white', opacity: 0.8 }} />
                        </Space>
                    </Col>
                </Row>
            </Card>

            {/* 统计数据卡片 */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} lg={4}>
                    <Card>
                        <Statistic
                            title="图片总数"
                            value={stats.totalImages}
                            prefix={<PictureOutlined style={{ color: '#1890ff' }} />}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={4}>
                    <Card>
                        <Statistic
                            title="标签数量"
                            value={stats.totalTags}
                            prefix={<TagsOutlined style={{ color: '#52c41a' }} />}
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={4}>
                    <Card>
                        <Statistic
                            title="待标记"
                            value={stats.untaggedImages}
                            prefix={<ClockCircleOutlined style={{ color: '#fa8c16' }} />}
                            valueStyle={{ color: stats.untaggedImages > 0 ? '#fa8c16' : '#52c41a' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={4}>
                    <Card>
                        <Statistic
                            title="未分发图片"
                            value={stats.undistributedImages}
                            prefix={<ClockCircleOutlined style={{ color: '#fa8c16' }} />}
                            valueStyle={{ color: stats.undistributedImages > 0 ? '#fa8c16' : '#52c41a' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={4}>
                    <Card>
                        <Statistic
                            title="设备类型"
                            value={stats.totalDeviceTypes}
                            prefix={<DesktopOutlined style={{ color: '#722ed1' }} />}
                            valueStyle={{ color: '#722ed1' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={4}>
                    <Card>
                        <Statistic
                            title="最近上传"
                            value={recentUploads.length}
                            prefix={<UploadOutlined style={{ color: '#eb2f96' }} />}
                            valueStyle={{ color: '#eb2f96' }}
                        />
                    </Card>
                </Col>
            </Row>

            <Row gutter={[24, 24]}>
                {/* 快速操作 */}
                <Col xs={24} lg={16}>
                    <Card
                        title={
                            <Space>
                                <RightOutlined />
                                <span>快速操作</span>
                            </Space>
                        }
                        extra={<Button type="link">查看全部</Button>}
                    >
                        <Row gutter={[16, 16]}>
                            {quickActions.map((action, index) => (
                                <Col xs={24} sm={12} lg={8} key={index}>
                                    <Card
                                        hoverable
                                        style={{
                                            textAlign: 'center',
                                            border: `1px solid ${action.color}20`,
                                            transition: 'all 0.3s ease'
                                        }}
                                        bodyStyle={{ padding: '20px 16px' }}
                                        onClick={() => handleQuickAction(action.path)}
                                    >
                                        <div
                                            style={{
                                                width: 56,
                                                height: 56,
                                                backgroundColor: action.bgColor,
                                                borderRadius: 8,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                margin: '0 auto 12px',
                                                fontSize: 24,
                                                color: action.color
                                            }}
                                        >
                                            {action.icon}
                                        </div>
                                        <Title level={5} style={{ marginBottom: 4 }}>
                                            {action.title}
                                        </Title>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            {action.description}
                                        </Text>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </Card>
                </Col>

                {/* 最近活动 */}
                <Col xs={24} lg={8}>
                    <Card
                        title={
                            <Space>
                                <ClockCircleOutlined />
                                <span>最近活动</span>
                            </Space>
                        }
                        extra={<Button type="link">查看更多</Button>}
                        style={{ height: 'fit-content' }}
                    >
                        <Timeline
                            items={getRecentActivities().map((activity: RecentActivity) => ({
                                dot: <div style={{
                                    width: 24,
                                    height: 24,
                                    backgroundColor: activity.color,
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    fontSize: 12
                                }}>
                                    {activity.icon}
                                </div>,
                                children: (
                                    <div>
                                        <div style={{ marginBottom: 4 }}>
                                            <Text strong>{activity.title}</Text>
                                        </div>
                                        <div style={{ marginBottom: 4 }}>
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                {activity.description}
                                            </Text>
                                        </div>
                                        <Text type="secondary" style={{ fontSize: 11 }}>
                                            {activity.time}
                                        </Text>
                                    </div>
                                )
                            }))}
                        />
                    </Card>
                </Col>
            </Row>

            {/* 系统状态和提示 */}
            <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
                <Col xs={24} lg={12}>
                    <Card title="系统状态" size="small">
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                    <Text>CPU使用率</Text>
                                    {/* <Text>{systemStatus.cpuUsage.toFixed(1)}%</Text> */}
                                </div>
                                <Progress
                                    percent={Number(systemStatus.cpuUsage.toFixed(2))}
                                    status={systemStatus.cpuUsage > 80 ? "exception" : "active"}
                                    strokeColor={systemStatus.cpuUsage > 80 ? "#ff4d4f" : "#52c41a"}
                                />
                            </div>
                            <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                    <Text>内存使用率</Text>
                                    {/* <Text>{systemStatus.memoryUsage.toFixed(1)}%</Text> */}
                                </div>
                                <Progress
                                    percent={Number(systemStatus.memoryUsage.toFixed(2))}
                                    status={systemStatus.memoryUsage > 80 ? "exception" : "active"}
                                    strokeColor={systemStatus.memoryUsage > 80 ? "#ff4d4f" : "#1890ff"}
                                />
                            </div>
                            <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                    <Text>标记完成度</Text>
                                    {/* <Text>{stats.totalImages > 0 ? ((stats.totalImages - stats.untaggedImages) / stats.totalImages * 100).toFixed(1) : 0}%</Text> */}
                                </div>
                                <Progress
                                    percent={stats.totalImages > 0 ? Number(((stats.totalImages - stats.untaggedImages) / stats.totalImages * 100).toFixed(2)) : 0}
                                    strokeColor="#13c2c2"
                                />
                            </div>
                        </Space>
                    </Card>
                </Col>
                <Col xs={24} lg={12}>
                    <Card title="系统提示" size="small">
                        <Space direction="vertical" style={{ width: '100%' }}>
                            {stats.untaggedImages > 0 && (
                                <Alert
                                    message={`有 ${stats.untaggedImages} 张图片待标记`}
                                    description="建议及时为图片添加标签以便管理"
                                    type="warning"
                                    showIcon
                                    action={
                                        <Button size="small" onClick={() => navigate('/image-list')}>
                                            去标记
                                        </Button>
                                    }
                                />
                            )}
                            {stats.undistributedImages > 0 && (
                                <Alert
                                    message={`有 ${stats.undistributedImages} 张图片未分发`}
                                    description="这些图片还未上架分发，可在图片管理页面进行操作"
                                    type="info"
                                    showIcon
                                    action={
                                        <Button size="small" onClick={() => navigate('/image-list')}>
                                            去分发
                                        </Button>
                                    }
                                />
                            )}
                            {systemStatus.cpuUsage > 80 && (
                                <Alert
                                    message="CPU使用率过高"
                                    description={`当前CPU使用率为 ${systemStatus.cpuUsage.toFixed(1)}%，建议检查系统负载`}
                                    type="warning"
                                    showIcon
                                />
                            )}
                            {systemStatus.memoryUsage > 80 && (
                                <Alert
                                    message="内存使用率过高"
                                    description={`当前内存使用率为 ${systemStatus.memoryUsage.toFixed(1)}%，建议释放内存或扩容`}
                                    type="warning"
                                    showIcon
                                />
                            )}
                            {stats.untaggedImages === 0 && stats.undistributedImages === 0 &&
                                systemStatus.cpuUsage <= 80 && systemStatus.memoryUsage <= 80 && (
                                    <Alert
                                        message="系统运行正常"
                                        description="所有服务正常运行，图片管理状态良好"
                                        type="success"
                                        showIcon
                                    />
                                )}
                        </Space>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default Dashboard; 